import React, { useState, useReducer, useEffect } from 'react';
import { PlatformLayout } from './components/PlatformLayout';
import { DemoHeader } from './components/DemoHeader';
import { Breadcrumb } from './components/Breadcrumb';
import { PatternSelector } from './components/PatternSelector';
import { FurnitureSelector } from './components/FurnitureSelector';
import { ResultsGallery } from './components/ResultsGallery';
import { SettingsCustomizer } from './components/SettingsCustomizer';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card } from './components/ui/card';
import { Pattern, FurnitureType, GeneratedResult, AppState } from './lib/types';
import { ArrowRight, Sparkles, Save, Share2, MoreHorizontal, Lock } from 'lucide-react';

// Mock data
const patterns: Pattern[] = [
  {
    id: 'pattern-1',
    name: 'Patio 714',
    description: '10847714',
    imageUrl: '/pattern-1.jpg'
  },
  {
    id: 'pattern-2',
    name: 'Juan-Les-Pins 145',
    description: '19605145',
    imageUrl: '/pattern-2.jpg'
  },
  {
    id: 'pattern-3',
    name: 'Tiramisu 143',
    description: '3487',
    imageUrl: '/pattern-3.jpg'
  }
];

const furnitureTypes: FurnitureType[] = [
  {
    id: 'chair',
    name: 'Chair',
    icon: 'chair',
    description: 'Accent chairs and seating'
  },
  {
    id: 'curtains',
    name: 'Curtains',
    icon: 'curtains',
    description: 'Window treatments'
  }
];

const initialState: AppState = {
  selectedPattern: patterns[0], // Default to first pattern
  selectedFurniture: furnitureTypes[0], // Default to first furniture type
  isLoading: false,
  results: [],
  selectedResult: null,
  customSetting: '',
  currentStep: 'selection'
};

type AppAction = 
  | { type: 'SELECT_PATTERN'; pattern: Pattern }
  | { type: 'SELECT_FURNITURE'; furniture: FurnitureType }
  | { type: 'START_LOADING' }
  | { type: 'SET_RESULTS'; results: GeneratedResult[] }
  | { type: 'SELECT_RESULT'; result: GeneratedResult }
  | { type: 'SET_CUSTOM_SETTING'; setting: string }
  | { type: 'GO_TO_RESULTS' }
  | { type: 'RESET' };

function appReducer(state: AppState, action: AppAction): AppState {
  switch (action.type) {
    case 'SELECT_PATTERN':
      return { ...state, selectedPattern: action.pattern };
    case 'SELECT_FURNITURE':
      return { ...state, selectedFurniture: action.furniture };
    case 'START_LOADING':
      return { ...state, isLoading: true, currentStep: 'results' };
    case 'SET_RESULTS':
      return { ...state, isLoading: false, results: action.results };
    case 'SELECT_RESULT':
      return { ...state, selectedResult: action.result, currentStep: 'customization' };
    case 'SET_CUSTOM_SETTING':
      return { ...state, customSetting: action.setting };
    case 'GO_TO_RESULTS':
      return { ...state, currentStep: 'results', selectedResult: null };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

function App() {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Check for existing authentication cookie on mount
  useEffect(() => {
    const authCookie = document.cookie
      .split('; ')
      .find(row => row.startsWith('trilo_auth='));
    
    if (authCookie && authCookie.split('=')[1] === 'true') {
      setIsAuthenticated(true);
    }
    setIsCheckingAuth(false);
  }, []);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.toUpperCase() === 'TRILO') {
      // Set cookie that expires in 30 days
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);
      document.cookie = `trilo_auth=true; expires=${expiryDate.toUTCString()}; path=/`;
      
      setIsAuthenticated(true);
      setPasswordError('');
    } else {
      setPasswordError('Invalid code. Please try again.');
      setPassword('');
    }
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show password screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-8">
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 mb-4 flex items-center justify-center">
              <img src="/trilo.svg" alt="Trilo" className="w-full h-full" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Required</h1>
            <p className="text-gray-600">Please enter the access code to continue</p>
          </div>
          
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Enter access code"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setPasswordError('');
                }}
                className={passwordError ? 'border-red-500' : ''}
                autoFocus
              />
              {passwordError && (
                <p className="text-red-500 text-sm mt-2">{passwordError}</p>
              )}
            </div>
            
            <Button type="submit" className="w-full">
              UNLOCK ACCESS
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  const handleDiscover = async () => {
    if (!state.selectedPattern || !state.selectedFurniture) return;

    dispatch({ type: 'START_LOADING' });

    // Simulate API call
    setTimeout(() => {
      const mockResults: GeneratedResult[] = Array.from({ length: 4 }, (_, i) => ({
        id: `result-${state.selectedPattern!.id}-${state.selectedFurniture!.id}-${i + 1}`,
        patternId: state.selectedPattern!.id,
        furnitureType: state.selectedFurniture!.id,
        imageUrl: `/${state.selectedPattern!.id}/${state.selectedFurniture!.id}-${i + 1}.png`,
        thumbnail: `/${state.selectedPattern!.id}/${state.selectedFurniture!.id}-${i + 1}.png`
      }));

      dispatch({ type: 'SET_RESULTS', results: mockResults });
    }, 2000);
  };

  const canDiscover = state.selectedPattern && state.selectedFurniture;

  const getBreadcrumbItems = () => {
    switch (state.currentStep) {
      case 'selection':
        return [
          { label: 'Pattern Library', active: true }
        ];
      case 'results':
        return [
          { 
            label: 'Pattern Library',
            onClick: () => dispatch({ type: 'RESET' })
          },
          { label: 'Visualization Results', active: true }
        ];
      case 'customization':
        return [
          { 
            label: 'Pattern Library',
            onClick: () => dispatch({ type: 'RESET' })
          },
          { 
            label: 'Visualization Results',
            onClick: () => dispatch({ type: 'GO_TO_RESULTS' })
          },
          { label: 'Environment Settings', active: true }
        ];
      default:
        return [];
    }
  };

  return (
    <PlatformLayout>
      <Breadcrumb items={getBreadcrumbItems()} />
      
      {/* Page Header with Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {state.currentStep === 'selection' && 'Pattern Visualization Studio'}
            {state.currentStep === 'results' && 'Generated Visualizations'}
            {state.currentStep === 'customization' && 'Environment Configuration'}
          </h1>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            {state.currentStep === 'selection' && 'Create stunning furniture visualizations with custom patterns'}
            {state.currentStep === 'results' && 'Select your preferred visualization to continue'}
            {state.currentStep === 'customization' && 'Preview your design in different environments'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled className="disabled-element">
            <Save className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Save Project</span>
            <span className="sm:hidden">Save</span>
          </Button>
          <Button variant="outline" size="sm" disabled className="disabled-element">
            <Share2 className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Share</span>
          </Button>
          <Button variant="outline" size="sm" disabled className="disabled-element">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {state.currentStep === 'selection' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <PatternSelector
              patterns={patterns}
              selectedPattern={state.selectedPattern}
              onSelect={(pattern) => dispatch({ type: 'SELECT_PATTERN', pattern })}
            />
            
            <FurnitureSelector
              furnitureTypes={furnitureTypes}
              selectedFurniture={state.selectedFurniture}
              onSelect={(furniture) => dispatch({ type: 'SELECT_FURNITURE', furniture })}
            />
          </div>
          
          <div className="flex justify-center pt-4 mb-16">
            <Button
              size="lg"
              onClick={handleDiscover}
              disabled={!canDiscover}
              className="px-6 py-2"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Discover Visualizations
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {state.currentStep === 'results' && (
        <ResultsGallery
          results={state.results}
          selectedResult={state.selectedResult}
          isLoading={state.isLoading}
          onSelect={(result) => dispatch({ type: 'SELECT_RESULT', result })}
          onGoBack={() => dispatch({ type: 'RESET' })}
        />
      )}

      {state.currentStep === 'customization' && (
        <SettingsCustomizer
          selectedResult={state.selectedResult}
          customSetting={state.customSetting}
          onSettingChange={(setting) => dispatch({ type: 'SET_CUSTOM_SETTING', setting })}
          onReset={() => dispatch({ type: 'GO_TO_RESULTS' })}
        />
      )}
    </PlatformLayout>
  );
}

export default App; 